/**
 * TemplatePreviewPane — the editor's right-hand "Preview" panel (Figma node
 * 554:46177): one card per step, step 1 rendered for a real roster entry.
 *
 * The BE route renders a SAVED template row
 * (POST /campaigns/{id}/outreach/templates/{template_id}/preview), so this
 * pane deliberately never renders the in-progress form text: what you preview
 * is what the sender will send. While the form is dirty it says so instead of
 * quietly showing stale words as if they were current.
 */
import { useId, useState } from 'react';
import { Envelope, Info } from '../../../design-system/icons';
import type { OutreachSignatureSource, OutreachTemplatePreview, OutreachTemplateStep } from '@discovr/types';
import { Label } from '../../../components/ui/label';
import { Skeleton } from '../../../components/ui/skeleton';
import { RichBody } from './richBody';
import { formatWait } from './TemplateStepEditor';
import { apiErrorCopy } from './sendLabels';

export interface TemplatePreviewEntry {
  entryId: string;
  username: string;
}

export interface TemplatePreviewPaneProps {
  /** null until the template has been saved once — preview needs a row id. */
  templateId: number | null;
  entries: TemplatePreviewEntry[];
  preview: (templateId: number, entryId: string) => Promise<OutreachTemplatePreview>;
  /** The form has unsaved edits, so the rendered words are the last saved ones. */
  dirty?: boolean;
  /** The full sequence — only step 1 is rendered through `preview`; later
   *  steps are shown raw, since they resolve per creator at send time. */
  steps?: OutreachTemplateStep[];
  /** The SAVED template's signature. Only captions where the signature came
   *  from when the preview omits `signatureSource`: the BE preview's body_text
   *  already ends with the one a send appends. */
  signature?: string | null;
}

function signatureSourceOf(result: OutreachTemplatePreview, signature: string | null | undefined): OutreachSignatureSource {
  return result.signatureSource ?? (signature?.trim() ? 'template' : 'mailbox');
}

export function TemplatePreviewPane({ templateId, entries, preview, dirty = false, steps = [], signature }: TemplatePreviewPaneProps) {
  const selectId = useId();
  const [entryId, setEntryId] = useState('');
  const [result, setResult] = useState<OutreachTemplatePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = async (nextEntryId: string) => {
    setEntryId(nextEntryId);
    setResult(null);
    setError(null);
    if (!nextEntryId || templateId == null) return;
    setLoading(true);
    try {
      setResult(await preview(templateId, nextEntryId));
    } catch (err) {
      setError(apiErrorCopy(err, 'Could not render this template.', 'template'));
    } finally {
      setLoading(false);
    }
  };

  const solo = steps.length <= 1;
  return (
    <div className="flex flex-col gap-3 border-t border-border bg-muted/30 p-6 lg:border-t-0 lg:border-l">
      <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Preview</p>

      {entries.length === 0 ? (
        <p className="text-caption-regular text-muted-foreground">
          Import creators into this campaign to preview a template against a real profile.
        </p>
      ) : (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={selectId}>Preview for</Label>
          <select
            id={selectId}
            value={entryId}
            disabled={templateId == null}
            onChange={(e) => void handleChange(e.target.value)}
            className="h-9 rounded-md border border-border bg-background px-3 text-body-sm-regular disabled:opacity-50"
          >
            <option value="">Choose a creator…</option>
            {entries.map((entry) => (
              <option key={entry.entryId} value={entry.entryId}>
                {entry.username}
              </option>
            ))}
          </select>
        </div>
      )}

      {templateId == null ? (
        <p className="text-caption-regular text-muted-foreground">
          Save this template to preview it — the preview renders the saved version, the same one the sender uses.
        </p>
      ) : dirty ? (
        <p className="text-caption-regular text-amber-600">
          Preview shows the last saved version. Save to preview your changes.
        </p>
      ) : null}

      {error ? <p className="text-caption-regular text-rose-500">{error}</p> : null}

      {loading ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : null}

      {result && !loading ? (
        <PreviewCard title={solo ? 'Message' : 'Step 1'}>
          <p className="text-body-sm-medium text-foreground">{result.subject}</p>
          {result.bodyHtml ? (
            <RichBody html={result.bodyHtml} className="text-body-sm-regular text-foreground" />
          ) : (
            <p className="text-body-sm-regular whitespace-pre-wrap text-foreground">{result.bodyText}</p>
          )}
          <SignatureCaption source={signatureSourceOf(result, signature)} />
        </PreviewCard>
      ) : null}

      {steps.slice(1).map((st) => (
        <PreviewCard key={st.stepNo} title={`Step ${st.stepNo}`} note={`Wait ${formatWait(st.delayDays)}`}>
          {st.bodyHtml ? (
            <RichBody html={st.bodyHtml} className="text-body-sm-regular text-foreground" />
          ) : (
            <pre className="whitespace-pre-wrap font-sans text-body-sm-regular text-foreground">{st.body}</pre>
          )}
          <p className="text-[11px] text-muted-foreground">Rendered per creator at send time</p>
        </PreviewCard>
      ))}

      <p className="flex items-start gap-2 text-caption-regular text-muted-foreground">
        <Info size={14} aria-hidden className="mt-0.5 shrink-0" />
        <span>Merge tags fill from this creator&apos;s row. Anything missing falls back to a neutral word, never a blank.</span>
      </p>
    </div>
  );
}

function PreviewCard({ title, note, children }: { title: string; note?: string; children: React.ReactNode }) {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <div className="flex items-center justify-between gap-2 border-b border-border bg-muted/40 px-3 py-2 text-caption-regular text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <Envelope size={14} /> {title}
        </span>
        {note ? <span>{note}</span> : null}
      </div>
      <div className="flex flex-col gap-2 p-4">{children}</div>
    </div>
  );
}

function SignatureCaption({ source }: { source: OutreachSignatureSource }) {
  if (source === 'none') return null;
  return (
    <>
      <p className="text-caption-regular text-muted-foreground">{`Signature · from ${source}`}</p>
      {source === 'mailbox' ? (
        <p className="text-caption-regular text-muted-foreground">The mailbox&apos;s own signature is appended at send time.</p>
      ) : null}
    </>
  );
}
